/* eslint-disable react-hooks/set-state-in-effect -- legitimate setState in mount-time
   admin data hydration + user-initiated CRUD event handlers. */
import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Trash2, KeyRound, UserPlus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export const UsersEditor = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newUser, setNewUser] = useState({ username: "", password: "" });
  const [pwTarget, setPwTarget] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  // Load runs once on mount; setState inside is expected for data hydration.
  useEffect(() => {
    load();
  }, [load]);

  // Event handler triggered by admin clicking "Add admin".
  const addUser = async () => {
    if (!newUser.username || !newUser.password) {
      toast.error("Username and password required");
      return;
    }
    setAdding(true);
    try {
      await api.post("/admin/users", { ...newUser, role: "admin" });
      toast.success("Admin added");
      setNewUser({ username: "", password: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add admin");
    } finally {
      setAdding(false);
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete admin “${u.username}”?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Admin removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove admin");
    }
  };

  const changePassword = async () => {
    if (!pwValue || pwValue.length < 6) {
      toast.error("Password must be 6+ characters");
      return;
    }
    setPwBusy(true);
    try {
      await api.put(`/admin/users/${pwTarget.id}/password`, { new_password: pwValue });
      toast.success("Password updated");
      setPwTarget(null);
      setPwValue("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change password");
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="users-editor">
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-white p-5">
        <div className="flex items-center gap-2">
          <UserPlus className="h-4 w-4 text-[#F35A1F]" />
          <h3 className="pf-display text-base">Add an admin</h3>
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Username</Label>
            <Input
              data-testid="users-editor-new-username"
              value={newUser.username}
              onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
              autoComplete="off"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Password (6+ chars)</Label>
            <Input
              type="password"
              data-testid="users-editor-new-password"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              autoComplete="new-password"
            />
          </div>
          <div className="flex items-end">
            <Button onClick={addUser} disabled={adding} data-testid="users-editor-add-button" className="h-10 rounded-xl bg-[#F35A1F] text-white hover:bg-[#D94A14] w-full">
              {adding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Add admin
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-[hsl(var(--border))] bg-white">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[hsl(var(--border))]">
          <h3 className="pf-display text-base">Admins</h3>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-[hsl(var(--muted-foreground))]" />}
        </div>
        <ul className="divide-y divide-[hsl(var(--border))]">
          {users.map((u) => (
            <li key={u.id} data-testid={`users-editor-row-${u.username}`} className="flex items-center justify-between px-5 py-3">
              <div>
                <p className="font-semibold">{u.username}</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                  {u.role} · added {new Date(u.created_at).toLocaleDateString()}
                  {user?.id === u.id && " · you"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => { setPwTarget(u); setPwValue(""); }} className="h-9 rounded-lg">
                  <KeyRound className="h-3.5 w-3.5 mr-1.5" /> Change password
                </Button>
                <Button type="button" variant="ghost" size="icon" onClick={() => removeUser(u)} disabled={user?.id === u.id || users.length <= 1} className="h-9 w-9 text-[hsl(var(--muted-foreground))] hover:text-[#D94A14]">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </li>
          ))}
          {!loading && users.length === 0 && (
            <li className="px-5 py-6 text-sm text-[hsl(var(--muted-foreground))]">No admins.</li>
          )}
        </ul>
      </div>

      <Dialog open={!!pwTarget} onOpenChange={(o) => { if (!o) setPwTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change password</DialogTitle>
            <DialogDescription>
              {pwTarget ? <>for <span className="font-semibold">{pwTarget.username}</span></> : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>New password</Label>
            <Input
              type="password"
              data-testid="users-editor-change-pw-input"
              value={pwValue}
              onChange={(e) => setPwValue(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwTarget(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={changePassword} disabled={pwBusy} data-testid="users-editor-change-pw-save" className="rounded-xl bg-[#F35A1F] text-white hover:bg-[#D94A14]">
              {pwBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Update password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersEditor;
